// src/lib/runHistory.ts

export type RunHistoryItem = {
  run_id: string;
  run_date: string | null;
  completed_at: string | null;
  started_at?: string | null;
  run_label?: string | null;
  environment?: string | null;
  status?: string | null;
  prompt_count?: number | null;
};

export type MetricDelta = {
  current: number;
  previous: number | null;
  delta: number | null;
  direction: "up" | "down" | "flat" | "new";
  label: string;
};

export function formatRunDate(run?: RunHistoryItem | null): string {
  if (!run) return "No run selected";

  // run_date is the reporting-period identity. completed_at may be later for
  // backfilled runs, so use it only when the reporting period is absent.
  const value = run.run_date || run.completed_at;
  if (!value) return "Unknown date";

  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00`)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function isCustomerSafeRunLabel(label?: string | null): boolean {
  if (!label) return false;

  const normalized = label.toLowerCase();

  const blockedTerms = [
    "test",
    "cloud-run",
    "cloud run",
    "job",
    "manual",
    "debug",
    "runner",
    "provider",
  ];

  return !blockedTerms.some((term) => normalized.includes(term));
}

export function getRunDisplayName(run?: RunHistoryItem | null): string {
  if (!run) return "No run selected";

  const date = formatRunDate(run);
  const label = run.run_label?.trim();

  if (label && isCustomerSafeRunLabel(label)) {
    return `${label} • ${date}`;
  }

  return date;
}

export function calculateDelta(
  current: number,
  previous: number | null | undefined,
  options?: {
    suffix?: string;
    decimals?: number;
    inverseGood?: boolean;
  }
): MetricDelta {
  const decimals = options?.decimals ?? 1;
  const suffix = options?.suffix ?? "";

  if (previous === null || previous === undefined || Number.isNaN(previous)) {
    return {
      current,
      previous: null,
      delta: null,
      direction: "new",
      label: "Baseline",
    };
  }

  
  const delta = current - previous;
  const rounded = Number(delta.toFixed(decimals));

  let direction: MetricDelta["direction"] = "flat";
  if (rounded > 0) direction = options?.inverseGood ? "down" : "up";
  if (rounded < 0) direction = options?.inverseGood ? "up" : "down";

  const sign = rounded > 0 ? "+" : "";
  const label =
    rounded === 0
      ? `No change`
      : `${sign}${rounded.toFixed(decimals)}${suffix}`;

  return {
    current,
    previous,
    delta: rounded,
    direction,
    label,
  };
}

export function getPreviousRun(
  runs: RunHistoryItem[],
  currentRunId?: string | null
): RunHistoryItem | null {
  if (!runs.length) return null;

  if (!currentRunId) {
    return runs[1] || null;
  }

  const currentIndex = runs.findIndex((run) => run.run_id === currentRunId);
  if (currentIndex < 0) return runs[1] || null;

  return runs[currentIndex + 1] || null;
}

export function getLatestRun(runs: RunHistoryItem[]): RunHistoryItem | null {
  return runs[0] || null;
}
